import { useEffect, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown, Trash2, Users, CalendarDays, Plane, MapPin } from "lucide-react";
import { AddToCalendar } from "./AddToCalendar";
import { WishCitiesDialog } from "./WishCitiesDialog";
import { differenceInCalendarDays, formatDistanceToNowStrict, parseISO, isValid } from "date-fns";
import { ContactsDialog } from "./ContactsDialog";
import { prettyDate } from "@/lib/geo";
import { PlanDialog } from "@/components/plans/PlanDialog";
import { ArrangeOffer } from "./ArrangeOffer";

const TripButton = ({ w, P, index }) => {
  const plan = P.planFor(w.country);
  const others = plan ? plan.members.filter((m) => m.user_id !== P.me) : [];
  return (
    <PlanDialog country={w.country} P={P} index={index}>
      <button className={`flex items-center gap-1.5 text-xs rounded-lg border px-2 py-1 transition-colors ${others.length ? "border-sky-400/50 bg-sky-400/10 text-sky-200" : "border-white/10 text-slate-300 hover:border-sky-400/40 hover:text-sky-200"}`} data-testid={`plan-open-${index}`}>
        <Plane size={13} /> {others.length ? `With ${others.map((m) => m.name.split(" ")[0]).join(", ")}` : "Invite"}
      </button>
    </PlanDialog>
  );
};

const n = (i) => String(i + 1).padStart(2, "0");

export const cityTitle = (c) => (c.length === 1 ? c[0] : c.length === 2 ? `${c[0]} & ${c[1]}` : `${c[0]}, ${c[1]} +${c.length - 2}`);

export const WishItem = ({ w, i, last, b, onMove, onFocus, P, cityFirst }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: w.id, disabled: b.readOnly });
  const [date, setDate] = useState(w.target_date);
  const [end, setEnd] = useState(w.end_date || "");
  useEffect(() => setDate(w.target_date), [w.target_date]);
  useEffect(() => setEnd(w.end_date || ""), [w.end_date]);
  const days = date && end && isValid(parseISO(end)) ? differenceInCalendarDays(parseISO(end), parseISO(date)) + 1 : 0;
  const pickStart = (v) => {
    // Moving the start past the end clears the end date
    const clear = end && v && end < v;
    setDate(v);
    if (clear || !v) setEnd("");
    b.patchWish(w.id, { target_date: v, ...(clear || !v ? { end_date: "" } : {}) });
  };
  const parsed = date ? parseISO(date) : null;
  const rel = parsed && isValid(parsed) ? formatDistanceToNowStrict(parsed, { addSuffix: true }) : null;

  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group rounded-xl border bg-white/[0.02] p-3 transition-colors duration-200 ${isDragging ? "border-amber-400/60 bg-amber-500/10 z-10 relative shadow-2xl" : "border-white/5 hover:border-amber-500/30"}`}
      data-testid={`want-to-go-item-${i + 1}`}>
      <div className="flex items-center gap-2">
        {!b.readOnly && <button {...attributes} {...listeners} className="text-slate-600 hover:text-slate-300 cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${i + 1}`} aria-label="Drag to reorder"><GripVertical size={16} /></button>}
        <span className="font-mono text-amber-400 text-sm w-6" data-testid={`want-to-go-rank-${i + 1}`}>{n(i)}</span>
        <button onClick={() => onFocus(w.country, cityFirst ? w.cities : undefined)} className="font-medium flex-1 min-w-0 text-left truncate hover:text-amber-300 transition-colors" data-testid={`want-to-go-country-${i + 1}`}>
          {cityFirst ? (
            <span data-testid={`want-to-go-city-title-${i + 1}`}>{cityTitle(w.cities)}<span className="ml-2 text-[11px] font-normal uppercase tracking-wider text-slate-500">{w.country}</span></span>
          ) : w.country}
        </button>
        {!b.readOnly && <div className="flex items-center">
          <button disabled={i === 0} onClick={() => onMove(i, i - 1)} className="p-1 text-slate-500 hover:text-white disabled:opacity-20" data-testid={`reorder-up-button-${i + 1}`} aria-label="Move up"><ChevronUp size={16} /></button>
          <button disabled={last} onClick={() => onMove(i, i + 1)} className="p-1 text-slate-500 hover:text-white disabled:opacity-20" data-testid={`reorder-down-button-${i + 1}`} aria-label="Move down"><ChevronDown size={16} /></button>
          <button onClick={() => b.delWish(w.id)} className="p-1 text-slate-500 hover:text-red-400" data-testid={`wish-delete-${i + 1}`} aria-label="Remove"><Trash2 size={14} /></button>
        </div>}
      </div>
      {b.readOnly ? (
        <div className="flex items-center gap-2 mt-2 pl-8 font-mono text-[11px] text-slate-400" data-testid={`wish-date-readonly-${i + 1}`}>
          {date ? <><CalendarDays size={12} /> {days ? `${prettyDate(date, date.slice(0, 4) === end.slice(0, 4) ? "d MMM" : "d MMM yyyy")} – ${prettyDate(end, "d MMM yyyy")}` : prettyDate(date, "MMM yyyy")} <span className="text-slate-500">{rel}</span></> : <span className="text-slate-600">Date to be decided</span>}
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-2 mt-2 pl-[3.25rem]">
        <label className="flex flex-wrap items-center gap-1.5 text-xs text-slate-400 rounded-lg border border-white/10 px-2 py-1 hover:border-amber-500/40 transition-colors">
          <CalendarDays size={13} />
          <input type="date" value={date} onChange={(e) => pickStart(e.target.value)} aria-label="Trip start"
            className="bg-transparent outline-none text-slate-200 font-mono text-[11px] [color-scheme:dark]" data-testid={`wish-date-input-${i + 1}`} />
          {date && <>
            <span className="text-slate-500">to</span>
            <input type="date" value={end} min={date} onChange={(e) => { setEnd(e.target.value); b.patchWish(w.id, { end_date: e.target.value }); }} aria-label="Trip end"
              className="bg-transparent outline-none text-slate-200 font-mono text-[11px] [color-scheme:dark]" data-testid={`wish-end-date-input-${i + 1}`} />
          </>}
        </label>
        {rel && <span className="font-mono text-[10px] text-slate-400" data-testid={`wish-date-relative-${i + 1}`}>{rel}{days > 0 && ` · ${days} ${days === 1 ? "day" : "days"}`}</span>}
        <AddToCalendar w={w} index={i + 1} />
        <WishCitiesDialog w={w} b={b} index={i + 1}>
          <button className="flex items-center gap-1.5 text-xs rounded-lg border border-white/10 px-2 py-1 text-slate-300 hover:border-amber-500/40 hover:text-amber-200 transition-colors" data-testid={`wish-cities-open-${i + 1}`}>
            <MapPin size={13} /> {(w.cities || []).length ? `${w.cities.length} ${w.cities.length === 1 ? "city" : "cities"}` : "Cities"}
          </button>
        </WishCitiesDialog>
        <ContactsDialog w={w} b={b} index={i + 1}>
          <button className="flex items-center gap-1.5 text-xs rounded-lg border border-white/10 px-2 py-1 text-slate-300 hover:border-amber-500/40 hover:text-amber-200 transition-colors" data-testid={`contacts-open-${i + 1}`}>
            <Users size={13} /> {w.contacts.length} {w.contacts.length === 1 ? "contact" : "contacts"}
          </button>
        </ContactsDialog>
        <TripButton w={w} P={P} index={i + 1} />
      </div>
      )}
      {(w.cities || []).length > 0 && !(cityFirst && w.cities.length <= 2) && (
        <p className={`text-[11px] text-amber-200/80 mt-2 truncate ${b.readOnly ? "pl-8" : "pl-[3.25rem]"}`} data-testid={`wish-cities-${i + 1}`}>
          <MapPin size={10} className="inline mr-1 -mt-0.5" />{w.cities.join(" · ")}
        </p>
      )}
      {!b.readOnly && w.contacts.length > 0 && (
        <p className="text-[11px] text-slate-500 mt-2 pl-[3.25rem] truncate">{w.contacts.map((c) => c.name).join(" · ")}</p>
      )}
      {!b.readOnly && <ArrangeOffer w={w} b={b} P={P} index={i + 1} />}
    </li>
  );
};
